import { useRef } from "react";
import { Camera, FileText, X } from "lucide-react";

export const MAX_FILES = 5;
export const MAX_BYTES = 25 * 1024 * 1024;
const ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif,application/pdf,.doc,.docx,.xls,.xlsx";
const OK = /\.(jpe?g|png|webp|heic|heif|pdf|docx?|xlsx?)$/i;

const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const TONES = {
  dark: {
    label: "text-white",
    hint: "text-white",
    item: "border-white/15 text-white",
    muted: "text-white/70 hover:text-white",
    button: "border-white/40 text-white hover:bg-white/10 hover:border-white",
  },
  light: {
    label: "text-[#4A524D]",
    hint: "text-[#4A524D]",
    item: "border-black/10 bg-white/60 text-[#111413]",
    muted: "text-[#8A8F8B] hover:text-[#111413]",
    button: "border-[#0A2333]/40 text-[#0A2333] hover:bg-[#0A2333] hover:text-[#F9F8F4]",
  },
};

export const StatementPicker = ({
  files,
  onChange,
  onError,
  progress,
  label = "Latest statement (optional)",
  hint = "Snap a photo or attach a PDF of your super or investment statement",
  testid = "fee-check",
  tone = "dark",
}) => {
  const inputRef = useRef(null);
  const t = TONES[tone];

  const add = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    const valid = picked.filter((f) => {
      if (!OK.test(f.name)) return onError(`${f.name} isn't a photo, PDF, Word or Excel file.`), false;
      if (f.size > MAX_BYTES) return onError(`${f.name} is over 25 MB.`), false;
      return true;
    });
    const next = [...files, ...valid].slice(0, MAX_FILES);
    if (files.length + valid.length > MAX_FILES) onError(`You can attach up to ${MAX_FILES} files.`);
    onChange(next);
  };

  return (
    <div data-testid={`${testid}-statements`}>
      <p className={`block text-[10px] uppercase tracking-[0.25em] mb-1 ${t.label}`}>
        {label}
      </p>
      <p className={`text-sm leading-relaxed ${t.hint}`}>
        {hint} — up to {MAX_FILES} files, 25 MB each.
      </p>
      <input
        ref={inputRef}
        id={`${testid}-files`}
        data-testid={`${testid}-file-input`}
        type="file"
        accept={ACCEPT}
        multiple
        onChange={add}
        className="sr-only"
      />
      {files.length > 0 && (
        <ul className="mt-4 space-y-2" data-testid={`${testid}-file-list`}>
          {files.map((f, i) => (
            <li
              key={`${f.name}-${f.size}-${f.lastModified}`}
              data-testid={`${testid}-file-${i}`}
              className={`flex items-center gap-3 border px-4 py-3 text-sm ${t.item}`}
            >
              <FileText size={16} className="shrink-0 text-[#A78558]" />
              <span className="flex-1 truncate">{f.name}</span>
              <span className={`shrink-0 text-xs ${t.muted}`}>
                {progress?.[i] != null ? `${progress[i]}%` : fmtSize(f.size)}
              </span>
              {progress == null && (
                <button
                  type="button"
                  aria-label={`Remove ${f.name}`}
                  data-testid={`${testid}-remove-file-${i}`}
                  onClick={() => onChange(files.filter((_, j) => j !== i))}
                  className={`-m-2 p-2 ${t.muted}`}
                >
                  <X size={16} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {files.length < MAX_FILES && (
        <button
          type="button"
          data-testid={`${testid}-add-file-button`}
          onClick={() => inputRef.current?.click()}
          className={`mt-4 inline-flex items-center gap-2 rounded-full border px-5 py-3 text-xs uppercase tracking-[0.15em] transition-[background-color,border-color,color] duration-300 ${t.button}`}
        >
          <Camera size={15} />
          {files.length ? "Add another" : "Add photo or document"}
        </button>
      )}
    </div>
  );
};
